/**
 * Phone-only helper: turn wide data tables into stacked cards.
 *
 * The CSS in index.css does the layout work, it only needs two markers:
 *   - `data-mobile-cards` on a table that has a usable header row
 *   - `data-label="Column name"` on every body cell
 *
 * This module adds those markers for the ~90 tables in the app, watches for
 * tables rendered later (async data, dialogs), and stays idle above 767px.
 *
 * Referenced by SidebarLayout: `useEffect(() => startMobileTableLabels(), [])`.
 */

const PHONE_QUERY = "(max-width: 767px)";

function headerLabels(table) {
  const headRow = table.tHead?.rows?.[table.tHead.rows.length - 1];
  if (!headRow) return null;
  const labels = Array.from(headRow.cells).flatMap((cell) => {
    const span = cell.colSpan && cell.colSpan > 1 ? cell.colSpan : 1;
    const text = (cell.textContent || "").trim().replace(/\s+/g, " ");
    return Array(span).fill(text);
  });
  return labels.some((text) => text.length > 0) ? labels : null;
}

function labelTable(table) {
  const labels = headerLabels(table);
  if (!labels) {
    table.removeAttribute("data-mobile-cards");
    return;
  }
  table.setAttribute("data-mobile-cards", "");
  const bodies = table.tBodies.length ? Array.from(table.tBodies) : [];
  bodies.forEach((body) => {
    Array.from(body.rows).forEach((row) => {
      let column = 0;
      Array.from(row.cells).forEach((cell) => {
        const span = cell.colSpan && cell.colSpan > 1 ? cell.colSpan : 1;
        // A cell spanning the whole row (empty states, group headers) gets no label.
        const label = span > 1 ? "" : labels[column] || "";
        if (cell.getAttribute("data-label") !== label) {
          cell.setAttribute("data-label", label);
        }
        column += span;
      });
    });
  });
}

function labelAll(root) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll("table").forEach(labelTable);
}

export function startMobileTableLabels() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return undefined;
  }

  const media = window.matchMedia ? window.matchMedia(PHONE_QUERY) : null;
  let observer = null;
  let frame = null;

  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      labelAll(document);
    });
  };

  const stopObserving = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }
  };

  const clearMarkers = () => {
    document.querySelectorAll("table[data-mobile-cards]").forEach((table) => {
      table.removeAttribute("data-mobile-cards");
      table.querySelectorAll("td[data-label]").forEach((cell) => {
        cell.removeAttribute("data-label");
      });
    });
  };

  const apply = () => {
    const isPhone = media ? media.matches : false;
    if (!isPhone) {
      stopObserving();
      clearMarkers();
      return;
    }
    labelAll(document);
    if (!observer) {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  apply();

  if (media) {
    if (media.addEventListener) {
      media.addEventListener("change", apply);
    } else if (media.addListener) {
      media.addListener(apply);
    }
  }

  return () => {
    stopObserving();
    if (media) {
      if (media.removeEventListener) {
        media.removeEventListener("change", apply);
      } else if (media.removeListener) {
        media.removeListener(apply);
      }
    }
  };
}

export default startMobileTableLabels;
